// Dados fictícios do modo de demonstração (ver demoMode.ts) — usados só
// pra o proprietário ver o layout/gráficos/tabelas preenchidos antes de
// existir uma venda real. Nomes, valores e datas são exemplos, nunca
// dados de verdade.

import type {
  Affiliate,
  AdminOverview,
  AdminSaleRow,
  AffiliatePaymentsSummary,
  SaleListResult,
  SaleRow,
  SaleSummary,
  SeriesPoint,
  SyncResult,
} from './api';
import { labelForStatus } from './statusLabels';

function daysAgoIso(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString();
}

const DEMO_STATUSES = ['approved', 'complete', 'approved', 'refunded', 'approved', 'partially_refunded', 'canceled', 'waiting_payment'];

function buildSaleRow(index: number, overrides: Partial<SaleRow> = {}): SaleRow {
  const status = DEMO_STATUSES[index % DEMO_STATUSES.length];
  return {
    id: `demo-sale-${index}`,
    transaction: `HP1771569${(1000 + index).toString().padStart(4, '0')}`,
    product: 'CA Render',
    saleValue: 197,
    currency: 'BRL',
    commissionValue: 39.4,
    commissionTierLabel: '1-10',
    status,
    statusLabel: labelForStatus(status),
    purchasedAt: daysAgoIso(index * 2 + 1),
    approvedAt: daysAgoIso(index * 2 + 1),
    refundedAt: status === 'refunded' || status === 'partially_refunded' ? daysAgoIso(index) : null,
    canceledAt: status === 'canceled' ? daysAgoIso(index) : null,
    chargebackAt: null,
    ...overrides,
  };
}

const DEMO_SALE_ROWS: SaleRow[] = Array.from({ length: 8 }, (_, index) => buildSaleRow(index));

const DEMO_AFFILIATES_RAW = [
  { name: 'João Silva (exemplo)', couponCodes: ['JOAO20'], pixKey: 'joao.silva@exemplo.com' },
  { name: 'Maria Souza (exemplo)', couponCodes: ['MARIA15', 'MARIA_VIP'], pixKey: '123.456.789-00' },
  // Sem Pix de propósito — mostra o 'N/D' de quem ainda não preencheu
  // essa coluna na planilha (coluna opcional, ver worker/src/lib/affiliateSheet.mjs).
  { name: 'Pedro Santos (exemplo)', couponCodes: ['PEDRO10'], pixKey: null },
];

export const DEMO_AFFILIATES: Affiliate[] = DEMO_AFFILIATES_RAW.map((affiliate) => ({
  name: affiliate.name,
  email: `${affiliate.name.split(' ')[0].toLowerCase()}@exemplo.com`,
  couponCodes: affiliate.couponCodes,
  pixKey: affiliate.pixKey,
}));

function buildSeries(days: number): SeriesPoint[] {
  return Array.from({ length: days }, (_, index) => {
    const salesCount = index % 3 === 0 ? 0 : 1 + (index % 3);
    return {
      date: daysAgoIso(days - index).slice(0, 10),
      salesCount,
      revenue: salesCount * 197,
      commission: salesCount * 39.4,
    };
  });
}

const DEMO_SERIES = buildSeries(14);

export const DEMO_AFFILIATE_NAME = 'João Silva (exemplo)';
export const DEMO_AFFILIATE_COUPON_CODES = DEMO_AFFILIATES_RAW[0].couponCodes;

export function getDemoMySummary(): { summary: SaleSummary; affiliateName: string; couponCodes: string[] } {
  return {
    affiliateName: DEMO_AFFILIATE_NAME,
    couponCodes: DEMO_AFFILIATE_COUPON_CODES,
    summary: {
      salesCount: 5,
      refundsCount: 2,
      byCurrency: [{ currency: 'BRL', salesCount: 5, revenue: 985, commission: 197, netCommission: 118.2 }],
      seriesByCurrency: [{ currency: 'BRL', series: DEMO_SERIES }],
      // Exemplo: 12 vendas este mês → já na faixa 11-20 (25%), faltam 9
      // pra faixa 21+ (30%) — mostra como o aviso de faixa aparece no
      // dashboard do influenciador (recurso opt-in, ver
      // worker/src/lib/affiliateCommissionTiers.mjs).
      commissionTierStatus: {
        salesCountThisMonth: 12,
        tierLabel: '11-20',
        tierPercent: 25,
        nextTierLabel: '21+',
        nextTierPercent: 30,
        salesUntilNextTier: 9,
      },
    },
  };
}

export function getDemoMySales(): SaleListResult<SaleRow> {
  return { rows: DEMO_SALE_ROWS, page: 1, pageSize: 20, hasMore: false };
}

export function getDemoAdminOverview(): AdminOverview {
  return {
    totals: {
      salesCount: 15,
      approvedCount: 11,
      completeCount: 2,
      refundsCount: 3,
      chargebacksCount: 1,
      // Duas moedas de propósito, nunca somadas num único número — mostra
      // como fica no painel desde a correção de 2026-09-08 (uma venda em
      // USD estava sendo somada com vendas em BRL).
      byCurrency: [
        { currency: 'BRL', salesCount: 14, revenue: 2758, commission: 551.6, netAfterFees: 2427.5, netAfterFeesKnownCount: 14 },
        { currency: 'USD', salesCount: 1, revenue: 35.7, commission: 7.14, netAfterFees: 31.42, netAfterFeesKnownCount: 1 },
      ],
    },
    ranking: DEMO_AFFILIATES.map((affiliate, index) => ({
      name: affiliate.name,
      email: affiliate.email,
      refundsCount: index,
      byCurrency:
        index === 0
          ? [
              { currency: 'BRL', salesCount: 5, revenue: 5 * 197, commission: 5 * 39.4 },
              { currency: 'USD', salesCount: 1, revenue: 35.7, commission: 7.14 },
            ]
          : [{ currency: 'BRL', salesCount: 6 - index * 2, revenue: (6 - index * 2) * 197, commission: (6 - index * 2) * 39.4 }],
    })),
    // Tráfego pago (2026-09-30) — exemplo com 2 origens, pra mostrar a
    // tabela "Vendas por origem" preenchida no modo de demonstração.
    bySource: [
      { sourceCode: 'tiktok', byCurrency: [{ currency: 'BRL', salesCount: 3, revenue: 3 * 197, commission: 0 }] },
      { sourceCode: 'instagram', byCurrency: [{ currency: 'BRL', salesCount: 1, revenue: 197, commission: 0 }] },
    ],
    seriesByCurrency: [
      { currency: 'BRL', series: DEMO_SERIES },
      { currency: 'USD', series: [{ date: DEMO_SERIES[DEMO_SERIES.length - 1].date, salesCount: 1, revenue: 35.7, commission: 7.14 }] },
    ],
  };
}

export function getDemoAdminSales(): SaleListResult<AdminSaleRow> {
  const rows: AdminSaleRow[] = DEMO_SALE_ROWS.map((row, index) => {
    const affiliate = DEMO_AFFILIATES[index % DEMO_AFFILIATES.length];
    // Taxa da Hotmart exclusiva do admin (ver
    // worker/src/lib/affiliateAdminService.mjs) — só exemplo pra faixa
    // "aprovada"/"completa"; uma venda já reembolsada/cancelada mostra
    // 'N/D' aqui, do mesmo jeito que o backend faz (correção de
    // 2026-09-09: nunca um líquido "fantasma" pra venda já desfeita).
    const isRevenueStatus = row.status === 'approved' || row.status === 'complete';
    const hotmartFeeValue = isRevenueStatus && row.saleValue !== null ? Math.round(row.saleValue * 0.12 * 100) / 100 : null;
    const netAfterFeesValue =
      row.saleValue !== null && hotmartFeeValue !== null ? Math.round((row.saleValue - hotmartFeeValue) * 100) / 100 : null;
    return { ...row, affiliateName: affiliate.name, affiliateEmail: affiliate.email, hotmartFeeValue, netAfterFeesValue };
  });
  return { rows, page: 1, pageSize: 20, hasMore: false };
}

export function getDemoAffiliates(): Affiliate[] {
  return DEMO_AFFILIATES;
}

export function getDemoSyncResult(): SyncResult {
  return { configured: false };
}

const DEMO_PAYMENTS: AffiliatePaymentsSummary = {
  byCurrency: [{ currency: 'BRL', commission: 551.6, paid: 400, pending: 151.6 }],
  payments: [
    { id: 'demo-pay-1', amount: 250, currency: 'BRL', paidAt: daysAgoIso(20), note: 'Pix' },
    { id: 'demo-pay-2', amount: 150, currency: 'BRL', paidAt: daysAgoIso(5), note: null },
  ],
};

export function getDemoMyPayments(): AffiliatePaymentsSummary {
  return DEMO_PAYMENTS;
}

export function getDemoAdminAffiliatePayments(_affiliateEmail: string): AffiliatePaymentsSummary {
  return DEMO_PAYMENTS;
}
